import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import http from '../components/http/http';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';

interface FieldErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  consentGiven?: string;
}

interface TouchedFields {
  email: boolean;
  password: boolean;
  confirmPassword: boolean;
  firstName: boolean;
  lastName: boolean;
  phoneNumber: boolean;
  consentGiven: boolean;
}

const Register: React.FC = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    consentGiven: false,
    marketingConsent: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<string[] | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touched, setTouched] = useState<TouchedFields>({
    email: false,
    password: false,
    confirmPassword: false,
    firstName: false,
    lastName: false,
    phoneNumber: false,
    consentGiven: false,
  });
  const [isPhoneValid, setIsPhoneValid] = useState<boolean>(true);
  const navigate = useNavigate();

  const validateEmail = (email: string): string | undefined => {
    if (!email) {
      return t('emailRequired') || 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(email)) {
      return t('invalidEmail') || 'Invalid email address';
    }
    return undefined;
  };

  const checkPasswordRequirements = (password: string) => {
    return {
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[@$!%*?&\-]/.test(password),
    };
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) {
      return t('passwordRequired') || 'Password is required';
    }
    if (password.length < 8) {
      return t('passwordMinLength') || 'Password must contain at least 8 characters';
    }
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&\-])[A-Za-z\d@$!%*?&\-]+$/;
    if (!passwordRegex.test(password)) {
      return t('passwordPattern') || 'Password must contain uppercase and lowercase letters, numbers and special characters';
    }
    return undefined;
  };

  const validateConfirmPassword = (confirmPassword: string, password: string): string | undefined => {
    if (!confirmPassword) {
      return t('confirmPasswordRequired') || 'Confirmation password is required';
    }
    if (confirmPassword !== password) {
      return t('passwordsDoNotMatch') || 'Passwords do not match';
    }
    return undefined;
  };

  const validateFirstName = (firstName: string): string | undefined => {
    if (!firstName) {
      return t('firstNameRequired') || 'Name is required';
    }
    if (firstName.length < 2) {
      return t('firstNameMinLength') || 'Name must contain at least 2 characters';
    }
    if (firstName.length > 50) {
      return t('firstNameMaxLength') || 'Name cannot exceed 50 characters';
    }
    const nameRegex = /^[a-zA-Zа-яёА-ЯЁ\s-]+$/;
    if (!nameRegex.test(firstName)) {
      return t('firstNamePattern') || 'Name can only contain letters, spaces, and hyphens';
    }
    return undefined;
  };

  const validateLastName = (lastName: string): string | undefined => {
    if (!lastName) {
      return undefined;
    }
    if (lastName.length < 2) {
      return t('lastNameMinLength') || 'Surname must contain at least 2 characters';
    }
    if (lastName.length > 50) {
      return t('lastNameMaxLength') || 'Surname cannot exceed 50 characters';
    }
    const nameRegex = /^[a-zA-Zа-яёА-ЯЁ\s-]+$/;
    if (!nameRegex.test(lastName)) {
      return t('lastNamePattern') || 'Surname can only contain letters, spaces, and hyphens';
    }
    return undefined;
  };

  const validatePhoneNumber = (phoneNumber: string, isValid?: boolean): string | undefined => {
    if (!phoneNumber) {
      return undefined;
    }
    if (isValid === false) {
      return t('invalidPhoneNumber') || 'Invalid phone number - please complete the full number';
    }
    if (phoneNumber.length < 8) {
      return t('invalidPhoneNumber') || 'Invalid phone number';
    }
    return undefined;
  };

  const validateField = (name: string, value: any): string | undefined => {
    switch (name) {
      case 'email':
        return validateEmail(value);
      case 'password':
        return validatePassword(value);
      case 'confirmPassword':
        return validateConfirmPassword(value, form.password);
      case 'firstName':
        return validateFirstName(value);
      case 'lastName':
        return validateLastName(value);
      case 'phoneNumber':
        return validatePhoneNumber(value);
      case 'consentGiven':
        if (!value) {
          return t('consentRequired') || 'You must give consent to process personal data';
        }
        return undefined;
      default:
        return undefined;
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, form[name as keyof typeof form]);
    setFieldErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    
    setForm(prev => ({
      ...prev,
      [name]: newValue
    }));

    if (touched[name as keyof TouchedFields]) {
      const error = validateField(name, newValue);
      setFieldErrors(prev => ({ ...prev, [name]: error }));
    }

    if (name === 'password' && touched.confirmPassword) {
      const confirmError = validateConfirmPassword(form.confirmPassword, newValue as string);
      setFieldErrors(prev => ({ ...prev, confirmPassword: confirmError }));
    }

    if (name === 'confirmPassword') {
      const confirmError = validateConfirmPassword(newValue as string, form.password);
      setFieldErrors(prev => ({ ...prev, confirmPassword: confirmError }));
    }
  };

  const isFormValid = (): boolean => {
    const requiredFieldsFilled = 
      form.email.trim() !== '' &&
      form.password.trim() !== '' &&
      form.confirmPassword.trim() !== '' &&
      form.firstName.trim() !== '' &&
      form.consentGiven;

    if (!requiredFieldsFilled) {
      return false;
    }

    const passwordRequirements = checkPasswordRequirements(form.password);
    const passwordMeetsAllRequirements = 
      passwordRequirements.minLength &&
      passwordRequirements.hasUpperCase &&
      passwordRequirements.hasLowerCase &&
      passwordRequirements.hasNumber &&
      passwordRequirements.hasSpecialChar;

    if (!passwordMeetsAllRequirements) {
      return false;
    }

    const passwordsMatch = form.password === form.confirmPassword;
    if (!passwordsMatch) {
      return false;
    }

    const errors: FieldErrors = {};
    errors.email = validateEmail(form.email);
    errors.password = validatePassword(form.password);
    errors.confirmPassword = validateConfirmPassword(form.confirmPassword, form.password);
    errors.firstName = validateFirstName(form.firstName);
    errors.lastName = validateLastName(form.lastName);
    errors.phoneNumber = validatePhoneNumber(form.phoneNumber, isPhoneValid);
    errors.consentGiven = form.consentGiven ? undefined : (t('consentRequired') || 'You must give consent to process personal data');

    const hasErrors = Object.values(errors).some(error => error !== undefined);
    return !hasErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const allTouched = Object.keys(touched).reduce((acc, key) => {
      acc[key as keyof TouchedFields] = true;
      return acc;
    }, {} as TouchedFields);
    setTouched(allTouched);

    const errors: FieldErrors = {};
    errors.email = validateEmail(form.email);
    errors.password = validatePassword(form.password);
    errors.confirmPassword = validateConfirmPassword(form.confirmPassword, form.password);
    errors.firstName = validateFirstName(form.firstName);
    errors.lastName = validateLastName(form.lastName);
    errors.phoneNumber = validatePhoneNumber(form.phoneNumber, isPhoneValid);
    errors.consentGiven = form.consentGiven ? undefined : (t('consentRequired') || 'You must give consent to process personal data');
    
    setFieldErrors(errors);

    const hasErrors = Object.values(errors).some(error => error !== undefined);
    if (hasErrors) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setDetails(null);
    try {
      const data = await http.post('/api/users/auth/register', form);
      if (!data.success) {
        setError((data.errors && data.errors.join(', ')) || data.error || data.message || t('registrationFailed'));
        setDetails(data.details || null);
        setLoading(false);
        return;
      }
      setSuccess(data.message || t('registrationSuccessful'));
      setLoading(false);
      navigate('/register-success');
    } catch (err: any) {
      setError(t('networkError'));
      setDetails(null);
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 500, margin: '60px auto', padding: 24, border: '1px solid #eee', borderRadius: 8, background: '#fff' }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontFamily: 'Roboto, sans-serif' }}>{t('signUp')}</h2>
      </div>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 16 }}>
          <label>{t('email')}<span style={{ color: 'red' }}> *</span><br />
            <div style={{ position: 'relative' }}>
              <input 
                type="email" 
                name="email" 
                value={form.email} 
                onChange={handleChange}
                onBlur={handleBlur}
                required 
                style={{ 
                  width: '100%', 
                  padding: 8, 
                  paddingRight: touched.email && !fieldErrors.email && form.email ? 30 : 8,
                  marginTop: 4, 
                  fontSize: 14,
                  border: touched.email && fieldErrors.email ? '1px solid red' : touched.email && !fieldErrors.email && form.email ? '1px solid #4caf50' : '1px solid #ccc',
                  borderRadius: 4
                }} 
                placeholder={t('email')} 
              />
              {touched.email && !fieldErrors.email && form.email && (
                <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', marginTop: 4, color: '#4caf50' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </span>
              )}
            </div>
            {touched.email && fieldErrors.email && (
              <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                {fieldErrors.email}
              </div>
            )}
          </label>
        </div>
        <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
          <label style={{ flex: 1 }}>{t('firstName')}<span style={{ color: 'red' }}> *</span><br />
            <div style={{ position: 'relative' }}>
              <input 
                type="text" 
                name="firstName" 
                value={form.firstName} 
                onChange={handleChange}
                onBlur={handleBlur}
                required 
                style={{ 
                  width: '100%', 
                  padding: 8,
                  paddingRight: touched.firstName && !fieldErrors.firstName && form.firstName ? 30 : 8,
                  marginTop: 4, 
                  fontSize: 14,
                  border: touched.firstName && fieldErrors.firstName ? '1px solid red' : touched.firstName && !fieldErrors.firstName && form.firstName ? '1px solid #4caf50' : '1px solid #ccc',
                  borderRadius: 4
                }} 
                placeholder={t('firstName')} 
              />
              {touched.firstName && !fieldErrors.firstName && form.firstName && (
                <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', marginTop: 4, color: '#4caf50' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </span>
              )}
            </div>
            {touched.firstName && fieldErrors.firstName && (
              <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                {fieldErrors.firstName}
              </div>
            )}
          </label>
          <label style={{ flex: 1 }}>{t('lastName')}<br />
            <div style={{ position: 'relative' }}>
              <input 
                type="text" 
                name="lastName" 
                value={form.lastName} 
                onChange={handleChange}
                onBlur={handleBlur}
                style={{ 
                  width: '100%', 
                  padding: 8,
                  paddingRight: touched.lastName && !fieldErrors.lastName && form.lastName ? 30 : 8,
                  marginTop: 4, 
                  fontSize: 14,
                  border: touched.lastName && fieldErrors.lastName ? '1px solid red' : touched.lastName && !fieldErrors.lastName && form.lastName ? '1px solid #4caf50' : '1px solid #ccc',
                  borderRadius: 4
                }} 
                placeholder={t('lastName')} 
              />
              {touched.lastName && !fieldErrors.lastName && form.lastName && (
                <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', marginTop: 4, color: '#4caf50' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </span>
              )}
            </div>
            {touched.lastName && fieldErrors.lastName && (
              <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                {fieldErrors.lastName}
              </div>
            )}
          </label>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label>{t('phoneNumber')}<br />
            <div style={{ position: 'relative', marginTop: 4 }}>
              <PhoneInput
                country={'us'}
                value={form.phoneNumber}
                enableSearch={true}
                autoFormat={true}
                disableSearchIcon={true}
                isValid={(value, country: any) => {
                  if (!value || value.length === 0) {
                    setIsPhoneValid(true);
                    return true;
                  }
                  
                  const fullNumber = '+' + value;
                  try {
                    const isComplete = isValidPhoneNumber(fullNumber);
                    setIsPhoneValid(isComplete);
                    return isComplete;
                  } catch (error) {
                    setIsPhoneValid(false);
                    return false;
                  }
                }}
                onChange={(value, country: any) => {
                  setForm(prev => ({ ...prev, phoneNumber: value }));
                  
                  const fullNumber = '+' + value;
                  let isComplete = false;
                  try {
                    isComplete = isValidPhoneNumber(fullNumber);
                  } catch (error) {
                    isComplete = false;
                  }
                  
                  if (touched.phoneNumber) {
                    const error = validatePhoneNumber(value, isComplete);
                    setFieldErrors(prev => ({ ...prev, phoneNumber: error }));
                  }
                }}
                onBlur={() => {
                  setTouched(prev => ({ ...prev, phoneNumber: true }));
                  const error = validatePhoneNumber(form.phoneNumber, isPhoneValid);
                  setFieldErrors(prev => ({ ...prev, phoneNumber: error }));
                }}
                inputStyle={{
                  width: '100%',
                  padding: '8px 40px 8px 48px',
                  fontSize: 14,
                  border: touched.phoneNumber && fieldErrors.phoneNumber ? '1px solid red' : touched.phoneNumber && !fieldErrors.phoneNumber && form.phoneNumber && isPhoneValid ? '1px solid #4caf50' : '1px solid #ccc',
                  borderRadius: 4,
                  height: 'auto'
                }}
                buttonStyle={{
                  border: touched.phoneNumber && fieldErrors.phoneNumber ? '1px solid red' : touched.phoneNumber && !fieldErrors.phoneNumber && form.phoneNumber && isPhoneValid ? '1px solid #4caf50' : '1px solid #ccc',
                  borderRight: 'none',
                  borderRadius: '4px 0 0 4px',
                  background: '#fff'
                }}
                containerStyle={{
                  width: '100%',
                  position: 'relative'
                }}
                placeholder={t('phoneNumber') || 'Phone number'}
              />
              {touched.phoneNumber && !fieldErrors.phoneNumber && form.phoneNumber && isPhoneValid && (
                <span style={{ 
                  position: 'absolute', 
                  right: 12, 
                  top: '50%', 
                  transform: 'translateY(-50%)', 
                  marginTop: 2, 
                  color: '#4caf50', 
                  pointerEvents: 'none',
                  zIndex: 10
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </span>
              )}
            </div>
            {touched.phoneNumber && fieldErrors.phoneNumber && (
              <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                {fieldErrors.phoneNumber}
              </div>
            )}
          </label>
        </div>
        <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
          <label style={{ flex: 1 }}>{t('password')}<span style={{ color: 'red' }}> *</span><br />
            <div style={{ position: 'relative' }}>
              <input 
                type={showPassword ? 'text' : 'password'} 
                name="password" 
                value={form.password} 
                onChange={handleChange}
                onBlur={handleBlur}
                required 
                style={{ 
                  width: '100%', 
                  padding: 8, 
                  paddingRight: 35, 
                  marginTop: 4, 
                  fontSize: 14,
                  border: touched.password && fieldErrors.password ? '1px solid red' : touched.password && !fieldErrors.password && form.password ? '1px solid #4caf50' : '1px solid #ccc',
                  borderRadius: 4
                }} 
                placeholder={t('password')} 
              />
              <span
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  marginTop: 2,
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#666'
                }}
              >
                {!showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                    <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                    <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                    <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828" />
                    <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87" />
                    <path d="M3 3l18 18" />
                  </svg>
                )}
              </span>
            </div>
            {form.password && (
              <div style={{ marginTop: 8, fontSize: 12 }}>
                <div style={{ marginBottom: 4, color: '#666', fontWeight: 500 }}>
                  {t('passwordRequirements') || 'Password requirements:'}
                </div>
                {(() => {
                  const requirements = checkPasswordRequirements(form.password);
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: requirements.minLength ? '#4caf50' : '#999' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={requirements.minLength ? 'currentColor' : 'none'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          {requirements.minLength ? (
                            <path d="M20 6L9 17l-5-5" />
                          ) : (
                            <circle cx="12" cy="12" r="10" />
                          )}
                        </svg>
                        <span>{t('passwordRequirementMinLength') || 'At least 8 characters'}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: requirements.hasUpperCase ? '#4caf50' : '#999' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={requirements.hasUpperCase ? 'currentColor' : 'none'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          {requirements.hasUpperCase ? (
                            <path d="M20 6L9 17l-5-5" />
                          ) : (
                            <circle cx="12" cy="12" r="10" />
                          )}
                        </svg>
                        <span>{t('passwordRequirementUpperCase') || 'One uppercase letter'}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: requirements.hasLowerCase ? '#4caf50' : '#999' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={requirements.hasLowerCase ? 'currentColor' : 'none'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          {requirements.hasLowerCase ? (
                            <path d="M20 6L9 17l-5-5" />
                          ) : (
                            <circle cx="12" cy="12" r="10" />
                          )}
                        </svg>
                        <span>{t('passwordRequirementLowerCase') || 'One lowercase letter'}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: requirements.hasNumber ? '#4caf50' : '#999' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={requirements.hasNumber ? 'currentColor' : 'none'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          {requirements.hasNumber ? (
                            <path d="M20 6L9 17l-5-5" />
                          ) : (
                            <circle cx="12" cy="12" r="10" />
                          )}
                        </svg>
                        <span>{t('passwordRequirementNumber') || 'One number'}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: requirements.hasSpecialChar ? '#4caf50' : '#999' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={requirements.hasSpecialChar ? 'currentColor' : 'none'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          {requirements.hasSpecialChar ? (
                            <path d="M20 6L9 17l-5-5" />
                          ) : (
                            <circle cx="12" cy="12" r="10" />
                          )}
                        </svg>
                        <span>{t('passwordRequirementSpecialChar') || 'One special character (@$!%*?&-)'}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
            {touched.password && fieldErrors.password && (
              <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                {fieldErrors.password}
              </div>
            )}
          </label>
          <label style={{ flex: 1 }}>{t('confirmPassword')}<span style={{ color: 'red' }}> *</span><br />
            <div style={{ position: 'relative' }}>
              <input 
                type={showConfirmPassword ? 'text' : 'password'} 
                name="confirmPassword" 
                value={form.confirmPassword} 
                onChange={handleChange}
                onBlur={handleBlur}
                required 
                style={{ 
                  width: '100%', 
                  padding: 8, 
                  paddingRight: 35, 
                  marginTop: 4, 
                  fontSize: 14,
                  border: form.confirmPassword && form.password !== form.confirmPassword ? '1px solid red' : touched.confirmPassword && fieldErrors.confirmPassword ? '1px solid red' : touched.confirmPassword && !fieldErrors.confirmPassword && form.confirmPassword ? '1px solid #4caf50' : '1px solid #ccc',
                  borderRadius: 4
                }} 
                placeholder={t('confirmPassword')} 
              />
              <span
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  marginTop: 2,
                  userSelect: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#666'
                }}
              >
                {!showConfirmPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                    <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
                    <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                    <path d="M10.585 10.587a2 2 0 0 0 2.829 2.828" />
                    <path d="M16.681 16.673a8.717 8.717 0 0 1 -4.681 1.327c-3.6 0 -6.6 -2 -9 -6c1.272 -2.12 2.712 -3.678 4.32 -4.674m2.86 -1.146a9.055 9.055 0 0 1 1.82 -.18c3.6 0 6.6 2 9 6c-.666 1.11 -1.379 2.067 -2.138 2.87" />
                    <path d="M3 3l18 18" />
                  </svg>
                )}
              </span>
            </div>
            {(form.confirmPassword && form.password !== form.confirmPassword) || (touched.confirmPassword && fieldErrors.confirmPassword) ? (
              <div style={{ color: 'red', fontSize: 12, marginTop: 4 }}>
                {form.confirmPassword && form.password !== form.confirmPassword 
                  ? (t('passwordsDoNotMatch') || 'Passwords do not match')
                  : fieldErrors.confirmPassword}
              </div>
            ) : null}
          </label>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', lineHeight: '16px', fontSize: 14 }}>
            <input 
              type="checkbox" 
              name="consentGiven" 
              checked={form.consentGiven} 
              onChange={handleChange}
              onBlur={handleBlur}
              required 
              style={{ width: 'auto' }} 
            />
            <span>{t('iAgreeToTermsAndConditions')}<span style={{ color: 'red', marginLeft: 4 }}>*</span></span>
          </label>
          {touched.consentGiven && fieldErrors.consentGiven && (
            <div style={{ color: 'red', fontSize: 12, marginTop: 4, marginLeft: 24 }}>
              {fieldErrors.consentGiven}
            </div>
          )}
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
            <input type="checkbox" name="marketingConsent" checked={form.marketingConsent} onChange={handleChange} style={{ width: 'auto' }} />
            {t('iWantToStayAheadOfTheCurve')}
          </label>
        </div>
        {error && <div style={{ color: 'red', marginBottom: details ? 4 : 16 }}>{error}</div>}
        {details && details.length > 0 && (
          <ul style={{ color: 'red', marginBottom: 16, marginTop: 0, paddingLeft: 20 }}>
            {details.map((d, i) => <li key={i}>{d}</li>)}
          </ul>
        )}
        {success && <div style={{ color: 'green', marginBottom: 16 }}>{success}</div>}
        <div style={{ marginBottom: 16 }}>
          <button
            type="submit"
            disabled={loading || !isFormValid()}
            style={{
              width: '100%',
              padding: 10,
              background: loading || !isFormValid() ? '#ccc' : '#3baab0',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              fontWeight: 600,
              fontSize: 16,
              cursor: loading || !isFormValid() ? 'not-allowed' : 'pointer',
              transition: 'background 0.2s',
              opacity: loading || !isFormValid() ? 0.6 : 1
            }}
            onMouseOver={e => {
              if (!loading && isFormValid()) {
                e.currentTarget.style.background = '#329a9e';
              }
            }}
            onMouseOut={e => {
              if (!loading && isFormValid()) {
                e.currentTarget.style.background = '#3baab0';
              }
            }}
          >
            {loading ? t('registering') : t('signUp')}
          </button>
          </div>
      </form>
      <div style={{ textAlign: 'center', marginTop: 24, display: 'flex', flexDirection: 'column' }}>
        <span style={{ color: '#888', marginRight: 8 }}>{t('alreadyHaveAnAccount')}</span>
        <Link to="/login">
          <button
            style={{
              marginTop: 0,
              padding: '8px 20px',
              background: '#fff',
              color: '#3baab0',
              border: '2px solid #3baab0',
              borderRadius: 4,
              fontWeight: 600,
              fontSize: 16,
              cursor: 'pointer',
              transition: 'background 0.2s, color 0.2s',
            }}
            onMouseOver={e => {
              e.currentTarget.style.background = '#3baab0';
              e.currentTarget.style.color = '#fff';
            }}
            onMouseOut={e => {
              e.currentTarget.style.background = '#fff';
              e.currentTarget.style.color = '#3baab0';
            }}
          >
            {t('logIn')}
          </button>
        </Link>
      </div>
    </div>
  );
};

export default Register; 